<template>
  <div class="mt-4">
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark>
        <v-toolbar-title>단타의 신 · 유저 관리</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip small outlined dark class="mr-2">
          <v-icon left x-small>mdi-account-group</v-icon>
          총 {{ totalUserCount.toLocaleString() }}명
        </v-chip>
      </v-toolbar>

      <!-- 안내 -->
      <v-card-text class="pb-0 pt-3">
        <v-alert type="info" dense text class="mb-0 body-2">
          <strong>고객사</strong> 또는 <strong>매체</strong>를 선택하면 더 빠르고 안정적인 검색을 지원합니다. 매체 선택 시 해당 파티션 내에서 인덱스 검색으로 처리됩니다.
        </v-alert>
      </v-card-text>
      <!-- 필터/검색 영역 -->
      <v-card-text class="pb-0">
        <v-row dense align="center">
          <v-col cols="12" sm="3">
            <v-select
              v-model="filter.client_id"
              :items="clientOptions"
              label="고객사"
              outlined dense hide-details clearable
              item-text="name"
              item-value="id"
              @change="onFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="filter.app_key"
              :items="filteredMediaOptions"
              label="매체"
              outlined dense hide-details clearable
              item-text="name"
              item-value="id"
              @change="onFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
              v-model="filter.search_type"
              :items="searchTypeOptions"
              label="검색 타입"
              outlined dense hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="filter.keyword"
              label="검색어"
              outlined dense hide-details
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="onSearch"
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="1">
            <v-btn color="primary" depressed block @click="onSearch">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- 유저 리스트 -->
      <v-card-text class="pa-0 pt-2">
        <div style="overflow-x: auto;">
        <v-data-table
          :headers="headers"
          :items="userList"
          :items-per-page="50"
          :footer-props="{'items-per-page-options':[50, 100, 200]}"
          :loading="loading"
          :server-items-length="totalUserCount"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          class="elevation-0"
        >
          <template v-slot:item.client_id="{ item }">
            <span class="font-weight-medium">{{ item.client_id }}</span>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="item.status === 'active' ? 'green' : item.status === 'ban' ? 'red' : 'grey'" dark>
              {{ item.status === 'active' ? '활성' : item.status === 'ban' ? '정지' : '휴면' }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small @click="openUserDetail(item)" title="상세보기">
              <v-icon small>mdi-magnify</v-icon>
            </v-btn>
          </template>
        </v-data-table>
        </div>
        <v-container v-if="!loading && userList.length === 0" class="text-center py-8">
          <v-icon size="56" color="grey lighten-1">mdi-account-search</v-icon>
          <p class="text-body-1 grey--text mt-3">고객사와 매체를 선택한 뒤 검색해주세요.</p>
          <p class="text-caption grey--text">대량 유저 데이터는 매체별로 파티션되어 조회됩니다.</p>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- 유저 상세 다이얼로그 -->
    <v-dialog v-model="dialogDetail" max-width="600" scrollable>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-account</v-icon>
          유저 상세 정보
          <v-spacer></v-spacer>
          <v-chip small outlined>{{ selectedUser.user_id }}</v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-simple-table dense>
            <tbody>
              <tr><td class="font-weight-bold" style="width:130px">유저 아이디</td><td>{{ selectedUser.user_id }}</td></tr>
              <tr><td class="font-weight-bold">닉네임</td><td>{{ selectedUser.nickname }}</td></tr>
              <tr><td class="font-weight-bold">고객사</td><td>{{ selectedUser.client_name || selectedUser.client_id }}</td></tr>
              <tr><td class="font-weight-bold">매체</td><td>{{ selectedUser.media_name || selectedUser.app_key }}</td></tr>
              <tr><td class="font-weight-bold">가입일</td><td>{{ selectedUser.created_at }}</td></tr>
              <tr><td class="font-weight-bold">최종 접속</td><td>{{ selectedUser.last_login_at || '-' }}</td></tr>
              <tr><td class="font-weight-bold">상태</td><td>{{ selectedUser.status === 'active' ? '활성' : selectedUser.status === 'ban' ? '정지' : '휴면' }}</td></tr>
              <tr><td class="font-weight-bold">총 게임 참여</td><td>{{ selectedUser.game_count || 0 }}회</td></tr>
              <tr><td class="font-weight-bold">보유 포인트</td><td>{{ (selectedUser.point || 0).toLocaleString() }}</td></tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn small color="red" dark @click="banUser" v-if="selectedUser.status === 'active'">
            <v-icon left small>mdi-cancel</v-icon>이용 정지
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogDetail = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="py-12"></div>
    <div class="py-12"></div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
export default {
  name: 'Danta_UserList',
  data: () => ({
    loading: false,
    dialogDetail: false,
    totalUserCount: 0,
    userList: [],
    selectedUser: {},
    filter: {
      client_id: null,
      app_key: null,
      search_type: 'UNIQUE_KEY',
      keyword: '',
    },
    sortBy: ['created_at'],
    sortDesc: [true],
    headers: [
      { text: '유저 아이디', value: 'user_id', width: '140px' },
      { text: '닉네임', value: 'nickname', width: '120px' },
      { text: '고객사', value: 'client_name', width: '110px' },
      { text: '매체', value: 'media_name', width: '110px' },
      { text: '가입일', value: 'created_at', width: '150px' },
      { text: '최종 접속', value: 'last_login_at', width: '150px' },
      { text: '상태', value: 'status', width: '90px' },
      { text: '상세', value: 'actions', width: '60px', sortable: false },
    ],
    searchTypeOptions: [
      { text: '유저 고유키', value: 'UNIQUE_KEY' },
      { text: '유저 아이디', value: 'USER_ID' },
      { text: '닉네임', value: 'NICKNAME' },
      { text: '이메일', value: 'EMAIL' },
    ],
    // TODO: 백엔드 API에서 로드
    clientOptions: [],
    mediaOptions: [],
  }),
  computed: {
    filteredMediaOptions() {
      if (!this.filter.client_id) return this.mediaOptions;
      return this.mediaOptions.filter(m => m.client_id === this.filter.client_id);
    },
  },
  created() {
    this.loadClientOptions();
  },
  methods: {
    ...mapActions([
      'DANTA_GET_CLIENT_LIST',
      'DANTA_GET_MEDIA_LIST',
      'DANTA_SEARCH_USER',
      'DANTA_GET_USER_DETAIL',
      'DANTA_GET_USER_BY_UNIQUE_KEY',
      'DANTA_BAN_USER',
      'DANTA_GET_USER_COUNT',
    ]),
    async loadClientOptions() {
      try {
        const body_data = await this.DANTA_GET_CLIENT_LIST();
        this.clientOptions = (body_data.client_list || []).map(c => ({ name: c.client_name, id: c.client_id }));
      } catch (err) { console.error(err); }
    },
    async loadMediaOptions(client_id) {
      try {
        const body_data = await this.DANTA_GET_MEDIA_LIST({ client_id });
        this.mediaOptions = (body_data.media_list || []).map(m => ({ name: m.media_name, id: m.app_key, client_id: m.client_id }));
      } catch (err) { console.error(err); }
    },
    onFilterChange() {
      if (!this.filter.client_id) {
        this.filter.app_key = null;
        this.mediaOptions = [];
      } else {
        this.loadMediaOptions(this.filter.client_id);
      }
    },
    async onSearch() {
      if (!this.filter.keyword && !this.filter.client_id) {
        alert('고객사를 선택하거나 검색어를 입력해주세요.');
        return;
      }
      // 유저 고유키 검색: AES 복호화로 media_id+user_id 추출 → 파티션 직접 접근 (가장 빠름)
      // user_id 검색: 고객사+매체 선택 권장
      // 닉네임/이메일 검색: 매체 선택 권장 (파티션 프루닝)
      if (!['UNIQUE_KEY'].includes(this.filter.search_type) && !this.filter.app_key && this.filter.keyword) {
        if (!confirm('매체를 선택하지 않으면 검색이 느릴 수 있습니다. 계속하시겠습니까?')) return;
      }
      this.loading = true;
      try {
        let body_data;
        if (this.filter.search_type === 'UNIQUE_KEY' && this.filter.keyword) {
          body_data = await this.DANTA_GET_USER_BY_UNIQUE_KEY({ unique_key: this.filter.keyword });
          this.userList = body_data.user ? [body_data.user] : [];
          this.totalUserCount = this.userList.length;
        } else {
          body_data = await this.DANTA_SEARCH_USER({
            client_id: this.filter.client_id,
            app_key: this.filter.app_key,
            search_type: this.filter.search_type,
            keyword: this.filter.keyword,
            page: 1,
            page_size: 50,
          });
          this.userList = body_data.user_list || [];
          this.totalUserCount = body_data.total_count || this.userList.length;
        }
      } catch (err) {
        console.error(err);
        alert('유저 검색에 실패했습니다.');
      } finally {
        this.loading = false;
      }
    },
    openUserDetail(item) {
      this.selectedUser = JSON.parse(JSON.stringify(item));
      this.dialogDetail = true;
    },
    async banUser() {
      if (!confirm(`유저 [${this.selectedUser.user_id}]를 이용 정지하시겠습니까?`)) return;
      try {
        await this.DANTA_BAN_USER({
          client_id: this.selectedUser.client_id,
          app_key: this.selectedUser.app_key,
          user_id: this.selectedUser.user_id,
          status: 'BAN',
        });
        alert('이용 정지 처리되었습니다.');
        this.dialogDetail = false;
        this.onSearch();
      } catch (err) {
        console.error(err);
        alert('이용 정지에 실패했습니다.');
      }
    },
  },
};
</script>
